import { useMemo } from "react";

import PivotTable from "@Components/PivotTable";
import { formatters } from "@Components/hierarchical-report/utils";
import lossesStore from "@Modules/report/stores/losses";

import { useSessionPivotStore, SessionPivotProvider } from "./store";

function SessionPivotTable() {
  const { data, reportView, loading, useLoad } = useSessionPivotStore()

  const configure = useMemo(() => ({
    pivotby: {
      default: {
        cols: ['publisher'],
        rows: ['allocation'],
        metric: "cost",
        aggregator: 'sum',
        sort: 'asc',
        formatter: 'money',
      },
      options: {
        dimensions: [
          { value: 'publisher', label: 'Publisher' },
          { value: 'allocation', label: 'Allocation' },
          { value: 'campaignid', label: 'Campaign-Id' },
          { value: 'conversiontype', label: 'Conversion Type' },
          { value: 'payouttype', label: 'Payout Type' },
          { value: 'subId', label: 'SubID 1' },
          { value: 'subId2', label: 'SubID 2' },
          { value: 'subId3', label: 'SubID 3' },
          { value: 'duplicatedData', label: 'Duplicated Data' },
          { value: 'duplicatedClick', label: 'Duplicated Click' },
          { value: 'duplicated', label: 'Duplicated' },
          { value: 'dupeDaysDelta', label: 'Dupe Days Delta' },
          { value: 'converted', label: 'Converted' },
          { value: 'isLoss', label: 'isLoss' },

          { value: 'registration_completed', label: 'Completed' },
          { value: 'registrationTotalSteps', label: 'Total Steps' },
          { value: 'registrationLastStep', label: 'Last Step' },
          { value: 'tcpaConsent', label: 'TCPA Consent' },
          { value: 'skipRegistration', label: 'Skipped' },
          { value: 'registrationConsent', label: 'Reg. Consent' },

          { value: 'fraud_verification', label: 'Fraud' },
          { value: 'fraudScore', label: 'Fraud Score' },
          { value: 'emailVerification', label: 'Email Verification' },
          { value: 'addressVerification', label: 'Address Verification' },
          { value: 'botVerification', label: 'Bot Verification' },
          { value: 'postalVerification', label: 'Postal Verification' },
          { value: 'phoneVerification', label: 'Phone Verification' },

          { value: 'form', label: 'Form' },
          { value: 'formConversionStep', label: 'Conversion Step' },

          { value: 'day', label: 'Day' },
          { value: 'hour', label: 'Hour' },

          { value: 'device', label: 'Device' },
          { value: 'os', label: 'OS' },
          { value: 'browser', label: 'Browser' },

          { value: 'post', label: 'Post' },
          { value: 'post_version', label: 'Post Version' },
          { value: 'post_target', label: 'Post Target' },

          { value: 'state', label: 'State' },
          { value: 'gender', label: 'Gender' },
          { value: 'optout', label: 'Optout' },

          { value: 'action', label: 'Action' },
          { value: 'organization', label: 'Organization' },
          { value: 'site', label: 'Site' },

          {
            group: 'Meta Fields',
            options: lossesStore.metaField.fetchData,
          },
        ],
        metrics: [
          { value: 'cost', label: 'Cost', aggregator: true },
          { value: 'revenue', label: 'Revenue', aggregator: true },
          { value: 'margin', label: 'Margin', aggregator: true },
          { value: 'converted', label: 'Conversion' },
          { value: 'convertedTrue', label: 'Form Converted' },
          { value: 'duplicated', label: 'Duplicated' },
          { value: 'duplicatedData', label: 'Duplicated Data' },
          { value: 'duplicatedClick', label: 'Duplicated Click' },
          { value: 'tcpaconsent', label: 'TCPA Consent' },
          { value: 'completed', label: 'Completed' },
          { value: 'uniqUser', label: 'Unique Users' },
          { value: 'fraudConversion', label: 'Conv. Type is Fraud' },
          { value: 'fraudCheckTrue', label: 'Is fraud' },
          { value: 'fraudCheckFalse', label: 'Is Not fraud' },
          { value: 'fraudNotCheck', label: 'Fraud not verified' },
          { value: 'scrub', label: 'Scrub' },

          { value: 'rps', label: <span>RPS $ (<small><i>Rev. / Session</i></small>)</span>, aggregator: true },
          { value: 'rpu', label: <span>RPU $ (<small><i>Rev. / Users</i></small>)</span>, aggregator: true },

          { value: 'cps', label: <span>CPS $ (<small><i>Cost. / Session</i></small>)</span>, aggregator: true },
          { value: 'cpu', label: <span>CPU $ (<small><i>Cost. / Users</i></small>)</span>, aggregator: true },

          { value: 'cpc', label: <span>CPC $ (<small><i>Cost. / Conversion</i></small>)</span>, aggregator: true },
          { value: 'rpc', label: <span>RPC $ (<small><i>Rev. / Conversion</i></small>)</span>, aggregator: true },
          { value: 'cpfc', label: <span>CPFC $ (<small><i>Cost. / Form Converted</i></small>)</span>, aggregator: true },
          { value: 'rpfc', label: <span>RPFC $ (<small><i>Rev. / Form Converted</i></small>)</span>, aggregator: true },
        ],
      },
    },
    filterby: {
      options: [
        {
          value: 'publisher',
          get label() {
            return <formatters.Loadable isLoading={lossesStore.publisher.isLoading}>Publisher</formatters.Loadable>
          },
          options: lossesStore.publisher.fetchData,
        },
        {
          value: 'allocation',
          get label() {
            return <formatters.Loadable isLoading={lossesStore.allocation.isLoading}>Allocation</formatters.Loadable>
          },
          options: () => lossesStore.allocation.fetchData(),
        },
        {
          value: 'post_target',
          get label() {
            return <formatters.Loadable isLoading={lossesStore.post.isLoading}>Post target</formatters.Loadable>
          },
          options: () => lossesStore.post.fetchData(),
        },
        {
          value: 'post',
          get label() {
            return <formatters.Loadable isLoading={lossesStore.post.isLoading}>Post</formatters.Loadable>
          },
          options: () => lossesStore.post.fetchData(),
        },
        {
          value: 'payouttype',
          get label() {
            return <formatters.Loadable>Payout type</formatters.Loadable>
          },
          options: async () => [
            { value: "cpc", label: "CPC", getLabel: () => "CPC", getValue: () => "cpc" },
            { value: "cpa", label: "CPA", getLabel: () => "CPA", getValue: () => "cpa" },
            { value: "cpm", label: "CPM", getLabel: () => "CPM", getValue: () => "cpm" },
            { value: "cpl", label: "CPL", getLabel: () => "CPL", getValue: () => "cpl" },
            { value: "custom", label: "CUSTOM", getLabel: () => "CUSTOM", getValue: () => "custom" },
          ],
        },
        {
          value: 'fraudScore',
          get label() {
            return <formatters.Loadable>Fraud Score</formatters.Loadable>
          },
          options: async () => [
            { value: "good", label: "Good", getLabel: () => "Good", getValue: () => "good" },
            { value: "bad", label: "Bad", getLabel: () => "Bad", getValue: () => "bad" },
            { value: "warn", label: "Warn", getLabel: () => "Warn", getValue: () => "warn" },
            { value: "drop", label: "Drop", getLabel: () => "Drop", getValue: () => "drop" },
            { value: "throttle", label: "Throttle", getLabel: () => "Throttle", getValue: () => "throttle" },
            { value: "awaiting", label: "Awaiting", getLabel: () => "Awaiting", getValue: () => "awaiting" },
          ],
        },
      ],
    },
  }), [])

  return <PivotTable
    title="Session Pivot Table"
    configure={configure}
    useLoad={useLoad}
    reportView={reportView}
    isLoading={loading}
    data={data}
  />
}

export default function Layout() {
  return <SessionPivotProvider>
    <SessionPivotTable />
  </SessionPivotProvider>
}
